import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { MaterialIcon } from "./MaterialIcon";
import { useStorageQuota } from "@/hooks/useStorageQuota";
import styles from "./StorageBar.module.css";

const getBarColor = (percentage: number): { main: string; light: string } => {
  if (percentage <= 70) {
    return { main: "#52c41a", light: "#73d13d" };
  }
  if (percentage >= 95) {
    return { main: "#ff4d4f", light: "#ff7875" };
  }
  const ratio = (percentage - 70) / 25;
  const r = Math.round(82 + (255 - 82) * ratio);
  const g = Math.round(196 + (77 - 196) * ratio);
  const b = Math.round(26 + (79 - 26) * ratio);
  const rLight = Math.round(115 + (255 - 115) * ratio);
  const gLight = Math.round(209 + (120 - 209) * ratio);
  const bLight = Math.round(61 + (117 - 61) * ratio);
  return {
    main: `rgb(${r}, ${g}, ${b})`,
    light: `rgb(${rLight}, ${gLight}, ${bLight})`,
  };
};

const formatGB = (gb: number): string => {
  if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`;
  if (gb >= 10) return `${Math.round(gb)} GB`;
  return `${gb.toFixed(1)} GB`;
};

export const StorageBar = () => {
  const navigate = useNavigate();
  const { usedGB, totalGB, percentage, planName, loading, isV3, v3Breakdown } = useStorageQuota();

  const colors = getBarColor(percentage);

  // While the quota is being computed show a quiet dark-theme skeleton
  // instead of flashing fallback values (plan name / totals) that then jump
  // to the real ones once user data and plans config arrive.
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <MaterialIcon name="cloud" size={18} className={styles.icon} />
          <span className={styles.label}>Storage</span>
          <span className={`${styles.planBadge} ${styles.skeletonPulse}`}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        </div>
        <div className={`${styles.barContainer} ${styles.skeletonPulse}`} />
        <div className={styles.stats}>
          <span className={`${styles.skeletonBlock} ${styles.skeletonPulse}`} style={{ width: 32 }} />
          <span className={`${styles.skeletonBlock} ${styles.skeletonPulse}`} style={{ width: 72 }} />
        </div>
      </div>
    );
  }

  const handleClick = () => {
    navigate("/account");
  };

  const gradientStyle = {
    width: `${percentage}%`,
    background: `linear-gradient(90deg, ${colors.main} 0%, ${colors.light} 50%, ${colors.main} 100%)`,
  };

  const tooltipContent = isV3 && v3Breakdown ? (
    <div>
      <div>{v3Breakdown.activeViews} active view{v3Breakdown.activeViews !== 1 ? "s" : ""}</div>
      <div style={{ marginTop: 4, borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 4 }}>
        Total: {formatGB(totalGB)}
      </div>
    </div>
  ) : undefined;

  const bar = (
    <div className={styles.container} onClick={handleClick}>
      <div className={styles.header}>
        <MaterialIcon name="cloud" size={18} className={styles.icon} />
        <span className={styles.label}>Storage</span>
        <span className={styles.planBadge}>{planName}</span>
      </div>

      <div className={styles.barContainer}>
        <div className={styles.barFill} style={gradientStyle} />
      </div>

      <div className={styles.stats}>
        <span className={styles.percentage} style={{ color: colors.main }}>
          {percentage}%
        </span>
        <span className={styles.usage}>
          {formatGB(usedGB)} / {formatGB(totalGB)}
        </span>
      </div>

      {isV3 && v3Breakdown && (
        <div className={styles.breakdown}>
          {v3Breakdown.activeViews} view{v3Breakdown.activeViews !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );

  if (tooltipContent) {
    return <Tooltip title={tooltipContent} placement="right">{bar}</Tooltip>;
  }

  return bar;
};
